import type { FC } from "react"
import React from "react"

import { useTranslation } from "react-i18next"
import { useMatch, useNavigate, useLocation } from "react-router-dom"
import { useRecoilValue } from "recoil"

import Button from "~/components/Button"
import FormFooter from "~/components/FormFooter"
import { SpinnerCircle } from "~/components/Spinner"
import { useCanEditProposal } from "~/hooks/useCanEditProposal"

import { editingState } from "../../state/activity"
import type { UseDesign } from "../../state/designs/use-design"
import type { UseProposal } from "../../state/proposal/proposal-context"
import type { UseSite } from "../../state/site/site-context"

export const SetupMenuFormFooter: FC<{
  entitiesToUpdate?: (
    | ReturnType<UseDesign>
    | ReturnType<UseProposal>
    | ReturnType<UseSite>
  )[]
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => Promise<void> | void
  onCancel?: VoidFunction
  shouldCloseOnSave?: boolean
  isLoading: boolean
  isDisabled?: boolean
  shouldNavigateOnSuccess?: boolean
  isDirty?: boolean
}> = ({
  entitiesToUpdate,
  onSubmit,
  onCancel,
  isLoading,
  shouldCloseOnSave = true,
  shouldNavigateOnSuccess = true,
  isDisabled = false,
  isDirty = false,
}) => {
  const { t } = useTranslation()
  const editing = useRecoilValue(editingState)
  const canEdit = useCanEditProposal()
  const navigate = useNavigate()
  const location = useLocation()
  const isSetup = location.pathname.includes(`setup`)
  const routeMatch = useMatch(
    isSetup ? `proposal/:slug/setup/:stepId/:partId` : `proposal/:slug`,
  )
  const { stepId, slug } = routeMatch?.params || {}

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await onSubmit?.(event)
    const entitiesToSave: (Promise<void> | void)[] = (
      entitiesToUpdate ?? []
    ).map((entity) => entity.save())
    await Promise.all(entitiesToSave)
    if (shouldCloseOnSave && shouldNavigateOnSuccess) {
      if (isSetup) {
        navigate(`/proposal/${slug}/setup/${stepId}`)

        return
      }
      navigate(`/proposal/${slug}`)
    }
  }

  const cancel = () => {
    onCancel?.()
    if (isSetup) {
      navigate(`/proposal/${slug}/setup/${stepId}`)

      return
    }
    navigate(`/proposal/${slug}`)
  }

  return (
    <FormFooter>
      <Button disabled={isLoading} onClick={cancel}>
        {t(`cancel`)}
      </Button>
      <Button
        type="submit"
        onClick={submit}
        disabled={
          !canEdit || ((!editing || isLoading || isDisabled) && !isDirty)
        }
      >
        <p>
          {isLoading ? t(`saving`) : editing || isDirty ? t(`save`) : t(`saved`)}
        </p>
        {isLoading && (
          <SpinnerCircle
            size={20}
            backgroundColor="rgba(var(--white-rgb), 0.4)"
            spinnerColor="rgba(var(--white-rgb), 0.8)"
          />
        )}
      </Button>
    </FormFooter>
  )
}
